import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isValidPhone, normalizePhone } from '../utils/phone';
import { ErrorAlert } from '../components/ui/ErrorAlert';

interface RemovalRequest {
  id: string;
  phone_number: string;
  notes: string | null;
  status: 'UNDER_REVIEW' | 'REMOVED' | 'REJECTED';
  created_at: string;
}

export const RequestRemoval = () => {
  const { user } = useAuth();
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState<RemovalRequest[]>([]);
  const [loadingMy, setLoadingMy] = useState(false);

  const loadMyRequests = async () => {
    if (!user) return;
    setLoadingMy(true);
    try {
      const { data, error: dbError } = await supabase
        .from('removal_requests' as any)
        .select('id, phone_number, notes, status, created_at')
        .eq('requested_by', user.id)
        .order('created_at', { ascending: false });
      if (dbError) throw dbError;
      setMyRequests((data as any) ?? []);
    } catch (err) {
      // ignore
    } finally {
      setLoadingMy(false);
    }
  };

  useEffect(() => {
    loadMyRequests();
  }, [user]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!phone.trim()) {
      setError('Mobile number is required');
      return;
    }
    if (!isValidPhone(phone)) {
      setError('Enter a valid mobile number');
      return;
    }

    const normalizedPhone = normalizePhone(phone);
    setIsSubmitting(true);

    try {
      const payload: any = {
        phone_number: normalizedPhone,
        notes: notes.trim() || null,
      };
      if (user) payload.requested_by = user.id;

      const { error: dbError } = await supabase.from('removal_requests' as any).insert(payload);
      if (dbError) throw dbError;
      setSubmitted(true);
      setPhone('');
      setNotes('');
      loadMyRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full rounded border border-gray-300 px-3 py-2 text-sm';

  if (!user) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="mb-2 text-xl font-bold text-gray-900">Remove My Number</h1>
        <p className="mb-4 text-sm text-gray-600">
          Please{' '}
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-800">
            login
          </Link>{' '}
          to request removal of your phone number.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h1 className="mb-2 text-xl font-bold text-gray-900">Remove My Number</h1>
      <p className="mb-4 text-sm text-gray-600">
        Request removal of your phone number from the Phone Book. We will process your request within
        5 to 7 working days.
      </p>

      {submitted && (
        <div className="mb-4 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          Your request has been submitted. It will be processed within 5 to 7 working days.
        </div>
      )}

      {error && <ErrorAlert message={error} />}

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="tel"
          placeholder="Mobile number *"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={inputClass}
          required
        />
        <textarea
          placeholder="Reason / Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={`${inputClass} min-h-[100px]`}
          maxLength={500}
        />
        <p className="text-right text-xs text-gray-500">{notes.length}/500</p>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Removal Request'}
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-gray-600">
        Back to{' '}
        <Link to="/" className="font-medium text-blue-600 hover:text-blue-800">
          Phone Book
        </Link>
      </p>

      {user && myRequests.length > 0 && (
        <div className="mt-6 border-t border-gray-200 pt-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-900">My Removal Requests</h2>
          <ul className="space-y-2">
            {myRequests.map((req) => (
              <li key={req.id} className="rounded border border-gray-200 p-2 text-sm">
                <p className="font-medium text-gray-900">{req.phone_number}</p>
                <p className="text-xs text-gray-500">
                  Status: <span className="font-semibold capitalize">{req.status.toLowerCase().replace('_', ' ')}</span>
                </p>
                <p className="text-xs text-gray-500">
                  Submitted: {new Date(req.created_at).toLocaleDateString('en-IN')}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {user && loadingMy && <p className="mt-4 text-sm text-gray-500">Loading your requests...</p>}
    </div>
  );
};
